import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { PRIMARYCOLOR } from '../../Constants';
import { getMfaSettings, updateMfaSettings } from '../../services/accountSettingsApi';

const BRAND = PRIMARYCOLOR || '#005CC5';

export default function AccountSettings() {
  const [mfaEnabled, setMfaEnabled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [targetEnabled, setTargetEnabled] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [notice, setNotice] = useState({ open: false, message: '' });

  const loadMfa = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await getMfaSettings();
      if (response?.success !== 1 || typeof response?.mfa_enabled !== 'boolean') {
        throw new Error(response?.message || 'The MFA setting could not be loaded.');
      }
      setMfaEnabled(response.mfa_enabled);
    } catch (error) {
      setMfaEnabled(null);
      setLoadError(error?.message || 'Unable to load account security settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMfa();
  }, [loadMfa]);

  const openConfirmation = (enabled) => {
    setTargetEnabled(enabled);
    setPassword('');
    setShowPassword(false);
    setDialogError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setTargetEnabled(null);
    setPassword('');
    setShowPassword(false);
    setDialogError('');
  };

  const saveMfa = async (event) => {
    event.preventDefault();
    if (saving || targetEnabled === null || !password) return;

    setSaving(true);
    setDialogError('');
    try {
      const response = await updateMfaSettings(targetEnabled, password);
      if (response?.success !== 1 || typeof response?.mfa_enabled !== 'boolean') {
        throw new Error(response?.message || 'The MFA setting could not be updated.');
      }

      setMfaEnabled(response.mfa_enabled);
      setNotice({
        open: true,
        message: response.mfa_enabled
          ? 'Email MFA is now enabled for future sign-ins.'
          : 'Email MFA is now disabled for future sign-ins.',
      });
      setDialogOpen(false);
      setTargetEnabled(null);
      setPassword('');
      setShowPassword(false);
    } catch (error) {
      setDialogError(error?.message || 'Unable to update Email MFA.');
    } finally {
      setSaving(false);
    }
  };

  const enabling = targetEnabled === true;

  return (
    <>
      <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${alpha(BRAND, 0.13)}`, boxShadow: `0 16px 36px ${alpha('#16345C', 0.06)}` }}>
        <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box>
              <Typography sx={{ color: BRAND, fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Account
              </Typography>
              <Typography variant="h5" sx={{ mt: 0.4, fontWeight: 950, color: '#10243E' }}>Sign-in Security</Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: '#65758B', lineHeight: 1.65 }}>
                Control the additional verification required when signing in to your account.
              </Typography>
            </Box>
            <Chip icon={<SecurityRoundedIcon />} label="Personal setting" sx={{ color: BRAND, bgcolor: alpha(BRAND, 0.07), fontWeight: 800, '& .MuiChip-icon': { color: BRAND } }} />
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          {loadError ? (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={loadMfa}>Retry</Button>}
              sx={{ borderRadius: 3 }}
            >
              {loadError}
            </Alert>
          ) : (
            <Box
              sx={{
                p: { xs: 2, md: 2.4 },
                borderRadius: 3.5,
                border: `1px solid ${alpha(BRAND, 0.13)}`,
                bgcolor: alpha(BRAND, 0.025),
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: alpha(BRAND, 0.09), color: BRAND, flexShrink: 0 }}>
                    <EmailOutlinedIcon />
                  </Box>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontWeight: 900, color: '#10243E' }}>Email MFA</Typography>
                      {loading ? <Skeleton width={72} height={28} /> : (
                        <Chip
                          size="small"
                          label={mfaEnabled ? 'Enabled' : 'Disabled'}
                          color={mfaEnabled ? 'success' : 'default'}
                          variant={mfaEnabled ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 800 }}
                        />
                      )}
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.55, color: '#65758B', lineHeight: 1.6 }}>
                      Require a verification code sent to your email when signing in.
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#7A889B' }}>
                      This is tied to your account and applies across every team you access.
                    </Typography>
                  </Box>
                </Stack>

                {loading ? <Skeleton variant="rounded" width={58} height={34} /> : (
                  <Switch
                    checked={Boolean(mfaEnabled)}
                    onChange={(event) => openConfirmation(event.target.checked)}
                    disabled={mfaEnabled === null || saving}
                    inputProps={{ 'aria-label': 'Enable or disable Email MFA' }}
                  />
                )}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
        <Box component="form" onSubmit={saveMfa}>
          <DialogTitle sx={{ pb: 1 }}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Box sx={{ width: 38, height: 38, borderRadius: 2.25, display: 'grid', placeItems: 'center', bgcolor: alpha(BRAND, 0.09), color: BRAND }}>
                {enabling ? <CheckCircleOutlineRoundedIcon /> : <LockOutlinedIcon />}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 900, color: '#10243E' }}>{enabling ? 'Enable Email MFA' : 'Disable Email MFA'}</Typography>
                <Typography variant="caption" color="text.secondary">Confirm this account security change</Typography>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ pt: '12px !important' }}>
            <Typography variant="body2" sx={{ color: '#65758B', lineHeight: 1.65, mb: 2 }}>
              {enabling
                ? 'Future sign-ins will require a verification code sent to your email.'
                : 'Future sign-ins will use your email and password without an additional verification code.'}
              {' '}Your current session will remain signed in.
            </Typography>
            {dialogError ? <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert> : null}
            <TextField
              autoFocus
              fullWidth
              required
              label="Current password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={saving}
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={() => setShowPassword((current) => !current)} disabled={saving} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: 'none', fontWeight: 750 }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={!password || saving} sx={{ minWidth: 140, borderRadius: 2.5, textTransform: 'none', fontWeight: 850 }}>
              {saving ? <CircularProgress size={20} color="inherit" /> : enabling ? 'Enable MFA' : 'Disable MFA'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Snackbar
        open={notice.open}
        autoHideDuration={4000}
        onClose={() => setNotice((current) => ({ ...current, open: false }))}
        message={notice.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  );
}
